import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { cn } from "@/shared/lib/utils";
import { Award, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface Certificate {
  slug: string;
  src: string;
  /** Krátký titul karty. */
  title: string;
  /** Kdo certifikát vydal. */
  issuer: string;
  year: string;
  orientation: "landscape" | "portrait";
}

/**
 * Kurátorský výběr mezinárodních akreditací, licence a ocenění.
 * Skeny leží v public/hca-certifikaty (narovnané + web-optimalizované).
 */
const CERTIFICATES: readonly Certificate[] = [
  {
    slug: "expansion-2025-26",
    src: "/hca-certifikaty/cert-expansion-2025-26.jpg",
    title: "Highly Commended — Expansion Game 2025/26",
    issuer: "Hubbard College of Administration International",
    year: "2026",
    orientation: "landscape",
  },
  {
    slug: "highly-commended-2025",
    src: "/hca-certifikaty/cert-highly-commended-2025.jpg",
    title: "Highly Commended — HCA Czechia",
    issuer: "Hubbard College of Administration International",
    year: "2025",
    orientation: "portrait",
  },
  {
    slug: "second-place-2021-22",
    src: "/hca-certifikaty/cert-second-place-2021-22.jpg",
    title: "Certificate of Recognition — 2. místo, Expansion Game 2021/22",
    issuer: "Hubbard College of Administration International",
    year: "2021",
    orientation: "portrait",
  },
  {
    slug: "first-place-2020-21",
    src: "/hca-certifikaty/cert-first-place-2020-21.jpg",
    title: "First Place — Expansion Game 2020/21",
    issuer: "Hubbard College of Administration International",
    year: "2021",
    orientation: "landscape",
  },
  {
    slug: "king-endorsement",
    src: "/hca-certifikaty/cert-king-endorsement.jpg",
    title: "Endorsement Jeho Veličenstva krále Awulae Attibrukusu III",
    issuer: "Absolvent Hubbard College of Administration International",
    year: "2017",
    orientation: "portrait",
  },
  {
    slug: "license-2010",
    src: "/hca-certifikaty/cert-license-2010.jpg",
    title: "Licence k provozu Hubbard College Brno",
    issuer: "Board of Directors · Hubbard College of Administration International",
    year: "2010",
    orientation: "landscape",
  },
];

/** Sekce „Akreditace & ocenění" s mřížkou náhledů a lightboxem (Dialog). */
export function CertificatesGallery() {
  const gridRef = useGsapReveal<HTMLDivElement>({
    childrenSelector: "[data-cert-card]",
    stagger: 0.08,
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const current = openIndex === null ? undefined : CERTIFICATES[openIndex];

  const move = useCallback((dir: number) => {
    setOpenIndex((i) => (i === null ? i : (i + dir + CERTIFICATES.length) % CERTIFICATES.length));
  }, []);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, move]);

  return (
    <section
      className="mt-12"
      data-testid="hca-about-certificates"
      aria-label="Akreditace a ocenění"
    >
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
        <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
          Akreditace &amp; ocenění
        </div>
      </div>
      <h2 className="mt-2 max-w-3xl font-bold text-2xl tracking-tight sm:text-3xl">
        Licencované centrum a mezinárodně oceňovaná práce
      </h2>
      <p className="mt-3 max-w-3xl text-muted-foreground">
        HCA Moravia (HCA Czechia s.r.o.) působí od roku{" "}
        <strong className="text-foreground">2010</strong> pod licencí{" "}
        <strong className="text-foreground">Hubbard College of Administration International</strong>{" "}
        — součást mezinárodní sítě přibližně 80 vzdělávacích center. Naši práci opakovaně ocenily
        mezinárodní autority i absolventi z celého světa. Klikněte pro zvětšení.
      </p>

      <div ref={gridRef} className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
        {CERTIFICATES.map((cert, idx) => (
          <button
            key={cert.slug}
            type="button"
            data-cert-card
            onClick={() => setOpenIndex(idx)}
            className="group flex flex-col overflow-hidden rounded-lg border border-[color:var(--hca-gold)]/25 bg-card text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[color:var(--hca-gold)]/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--hca-gold)]"
            data-testid={`hca-about-certificate-${cert.slug}`}
            aria-label={`Zobrazit certifikát: ${cert.title} (${cert.year})`}
          >
            <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-white p-3">
              <img
                src={cert.src}
                alt={`${cert.title} — ${cert.issuer}, ${cert.year}`}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
              />
              <span className="absolute right-2 top-2 rounded-md bg-[color:var(--hca-navy)]/80 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </div>
            <div className="flex items-start gap-2 border-border border-t p-3">
              <Award
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--hca-gold)]"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <div className="font-semibold text-foreground text-xs leading-snug">
                  {cert.title}
                </div>
                <div className="mt-0.5 text-[0.7rem] text-muted-foreground">{cert.year}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog
        open={openIndex !== null}
        onOpenChange={(open) => {
          if (!open) setOpenIndex(null);
        }}
      >
        <DialogContent
          className="max-h-[90dvh] max-w-4xl gap-3 overflow-y-auto"
          data-testid="hca-about-certificate-lightbox"
        >
          {current ? (
            <>
              <DialogTitle className="pr-8 text-base">{current.title}</DialogTitle>
              <DialogDescription className="text-xs">
                {current.issuer} · {current.year}
              </DialogDescription>
              <div className="relative flex items-center justify-center rounded-md bg-white p-2 sm:p-4">
                <img
                  src={current.src}
                  alt={`${current.title} — ${current.issuer}, ${current.year}`}
                  className={cn(
                    "h-auto w-auto max-w-full object-contain",
                    current.orientation === "portrait" ? "max-h-[72vh]" : "max-h-[68vh]",
                  )}
                />
                <button
                  type="button"
                  onClick={() => move(-1)}
                  aria-label="Předchozí certifikát"
                  data-testid="hca-about-certificate-prev"
                  className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[color:var(--hca-navy)]/80 text-white backdrop-blur transition hover:bg-[color:var(--hca-navy)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--hca-gold)]"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => move(1)}
                  aria-label="Další certifikát"
                  data-testid="hca-about-certificate-next"
                  className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[color:var(--hca-navy)]/80 text-white backdrop-blur transition hover:bg-[color:var(--hca-navy)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--hca-gold)]"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-[color:var(--hca-navy)]/80 px-2.5 py-0.5 text-white text-xs tabular-nums">
                  {(openIndex ?? 0) + 1} / {CERTIFICATES.length}
                </span>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
